'use client'

import { createContext, useContext, useEffect, useState } from "react"

type Theme = "dark" | "light" | "system"

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "flightdeck-theme",
  ...props
}: ThemeProviderProps) {
  // Initialize with defaultTheme and update after mount
  const [theme, setTheme] = useState<Theme>(defaultTheme)
  const [mounted, setMounted] = useState(false)

  // Only access localStorage after component has mounted on the client
  useEffect(() => {
    setMounted(true)
    const savedTheme = localStorage?.getItem(storageKey) as Theme
    if (savedTheme) {
      setTheme(savedTheme)
    }
  }, [storageKey])

  // Apply theme class to document
  useEffect(() => {
    if (!mounted) return

    const root = document.documentElement
    root.classList.remove("light", "dark")

    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light"

      root.classList.add(systemTheme)
      return
    }

    root.classList.add(theme)
  }, [theme, mounted])

  // Listen for system theme changes when "system" is selected
  useEffect(() => {
    if (!mounted || theme !== "system") return

    function handleSystemThemeChange(event: MediaQueryListEvent) {
      const root = document.documentElement
      const systemTheme = event.matches ? "dark" : "light"
      
      root.classList.remove("light", "dark")
      root.classList.add(systemTheme)
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    
    // Modern API (with fallback for older browsers)
    mediaQuery.addEventListener("change", handleSystemThemeChange)
    
    return () => mediaQuery.removeEventListener("change", handleSystemThemeChange)
  }, [theme, mounted])

  // Avoid hydration mismatch by preventing rendering until mounted
  const value = {
    theme,
    setTheme: (newTheme: Theme) => {
      localStorage?.setItem(storageKey, newTheme)
      setTheme(newTheme)
    },
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider")
  }

  return context
}